/** @format */

import React, {useEffect, useRef, useState} from 'react';
import {useEditor, EditorContent} from '@tiptap/react';
import Document from '@tiptap/extension-document';
import StarterKit from '@tiptap/starter-kit';
import CharacterCount from '@tiptap/extension-character-count';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TEXTS } from "../../common/consts";
// we use our custom extension from extensions folder
import { Link } from './extensions/link/CustomLink';
import { CustomImage } from './extensions/image/CustomImage';
import TextAlign from '@tiptap/extension-text-align';
import { debounce } from 'lodash';
import './TipTapEditor.css';
import { getParsedData, resetEditorData, saveEditorDataToStore, setEditorInstance } from '../../reducers/textEditor';
import { setHighTopicCoverage, setLowTopicCoverage, setTopicCoverageData, updateMatchingWords } from '../../reducers/topicCoverage';
import { useDispatch, useSelector } from 'react-redux';
import { setWordCount } from '../../reducers/counters';
import { DEFAULT_ARTICLE_ID } from '../../common/consts';
import {
    getCurrentArticle,
    getCurrentArticleId,
    saveArticle,
    setArticle,
    setArticleId,
    setArticlesCollection,
    contentArticleData,
    outlineData,
    getArticleEditorLoadingState,
    getOutlineEditorLoadingState,
    saveOutlineToDB,
    getNewTitleForOutline,
    getOutlineData,
    getOutlineProgressStatus,
    setArticleAltered,
    checkIfArticleIsEditable,
    getArticleJustFinishedGenerating,
    getArticleCompleted,
    getArticleCompilationStatus,
    getCompletedHeadings,
    getTotalHeadings,
    getCurrentWebsiteArticle,
    getLastGeneratedArticle,
    getLoadingCurrentWebsiteArticle,
    getContentArticleData, applyWriterMode, getRegenerateCount,
    getArticleScore,
    getOutlineScore,
    getGeneratedScore, getDefaultTabSelected
} from '../../reducers/articles';
import { isDemoApplication } from '../../reducers/applicationMode';
import { checkTitleOptimization } from '../../reducers/titleOptimization';
import { checkHeadingsOptimization } from '../../reducers/headingsOptimization';
import { checkTopicDensity } from '../../reducers/topicDensity';
import { checkCurrentImages, transformContentOnPaste } from '../../reducers/images';

import useUnmount from '../../utils/useUnmount';
import { useApplicationType } from '../../utils/useApplicationType';
import EditorMenuTop from './EditorMenuTop';
import { loadingStep2Data } from '../../reducers/searchTopic';
import { useLocation } from 'react-router-dom';
import EditorHeader from './EditorHeader';
import AiCard from './AiCard/AiCard';
import {DraftIcon, RefreshIcon, RobotIcon, UserIcon, WriterMagicPen, WrittenFileIcon} from '../../UI_utils/SVGIcons';
import Scrollbars from 'react-custom-scrollbars';
import ArticleProgressBox from './ArticleProgressBox';
import {getGlobalState} from "../../globals";
import EditorContentInfo from "./EditorContentInfo/EditorContentInfo";
import {getUserHasWritingStyles} from "../../reducers/user";
import ArticleLoadingBlocks from "./ArticleLoadingBlocks";
import FeedbackBox from "./FeedbackBox";
import EditorSwitcher from "./EditorSwitcher";

const CustomDocument = Document.extend({
    content: 'heading block*',
});

const parseEditorData = debounce((dispatch, data) => {
    dispatch(saveEditorDataToStore(data));
}, 500);

const trackTopicCoverage = debounce((dispatch) => {
    dispatch(updateMatchingWords());
}, 600);

const sendDataToServer = debounce((dispatch, articleType) => {
    if(!articleType.viewOnly) {
        dispatch(saveArticle(articleType.type, 'article'));
    }
}, 500);

const sendOutlineDataToServer = debounce((dispatch, outline) => {
    dispatch(saveOutlineToDB(outline));
}, 500);

const trackTitle = debounce((dispatch) => {
    dispatch(checkTitleOptimization());
}, 900);

const trackHeadings = debounce((dispatch) => {
    dispatch(checkHeadingsOptimization());
}, 2800);

const trackTopicDensity = debounce((dispatch) => {
    dispatch(checkTopicDensity());
}, 2900);

const trackImages = debounce((dispatch) => {
    dispatch(checkCurrentImages());
}, 3000);

const TipTapEditor = ({ writerMode, switchToArticle, firstLoad, setShowPublishModal }) => {
    const articleScore = useSelector(getArticleScore);
    const outlineScore = useSelector(getOutlineScore);
    const generatedScore = useSelector(getGeneratedScore);

    const dispatch = useDispatch();
    const location = useLocation();
    const isDemoApp = useSelector(isDemoApplication);
    const styleAccess = useSelector(getUserHasWritingStyles)
    const currentArticleId = useSelector(getCurrentArticleId);
    const outlineProgressStatus = useSelector(getOutlineProgressStatus);
    const articleEditorIsEditable = useSelector(checkIfArticleIsEditable);
    const articleJustFinishedGenerating = useSelector(getArticleJustFinishedGenerating);

    const applicationType = useApplicationType();
    const articleLoading = useSelector(getArticleEditorLoadingState);
    const outlineLoading = useSelector(getOutlineEditorLoadingState);
    const articleData = useSelector(contentArticleData);
    const outline = useSelector(outlineData);
    const hasSharedToken = getGlobalState('sharedToken');

    const parsedData = useSelector(getParsedData);

    const lastGeneratedArticle = useSelector(getLastGeneratedArticle);
    const currentWebsiteArticle = useSelector(getCurrentWebsiteArticle);
    const loadingCurrentWebsiteArticle = useSelector(getLoadingCurrentWebsiteArticle);
    const regenerateCount = useSelector(getRegenerateCount);
    const defaultTabSelected = useSelector(getDefaultTabSelected);

    const [isOutline, setIsOutline] = React.useState(false);
    const [headingIsEmpty, setHeadingIsEmpty] = React.useState(true);
    const [generating, setGenerating] = React.useState(false);
    const [aiMode, setAiMode] = React.useState({ label: 'Open AI', value: 'openai' });
    const [selectedTestPrompt, setSelectedTestPrompt] = React.useState({ label: 'Default', value: false });
    const [selectedTypeOption, setSelectedTypeOption] = useState(defaultTabSelected)
    const [typeOptions, setTypeOptions] = useState([]);
    const [newTypeOptions, setNewTypeOptions] = useState([]);
    const [activeConfigurationStep, setActiveConfigurationStep] = useState(0)
    const [promptType, setPromptType] = useState('article')

    const articleCompilationStatus = useSelector(getArticleCompilationStatus);
    const articleCompleted = useSelector(getArticleCompleted);
    const completedHeadings = useSelector(getCompletedHeadings);
    const totalHeadings = useSelector(getTotalHeadings);

    useEffect(() => {
        if(defaultTabSelected !== selectedTypeOption) {
            setSelectedTypeOption(defaultTabSelected)
            dispatch(applyWriterMode(defaultTabSelected === 'outline' ? 'outline' : 'article'))
        }
    }, [defaultTabSelected])

    useEffect(() => {
        const outlineTooltip = TEXTS.OUTLINE_TAB_TOOLTOP;
        const generatedTooltip = TEXTS.GENERATED_TAB_TOOLTOP;
        const draftTooltip = TEXTS.DRAFT_TAB_TOOLTOP;

        // const defaultTabs = [{label: 'New article', key: 'new', tooltip: draftTooltip, score: articleScore}, {label: 'Current article', key:'current'}];
        // const generatedTabs = [{label: 'New article', key: 'new', tooltip: draftTooltip, score: articleScore}, {label: 'Generated', key: 'generated', tooltip: generatedTooltip, score: generatedScore}, {label: 'Current article', key:'current'}];
        const newDefaultTabs = [{label: 'Outline', key: 'outline', icon: WrittenFileIcon, score: outlineScore, tooltip: outlineTooltip}, {label: 'Working Draft', key:'new', icon: DraftIcon, score: articleScore, tooltip: draftTooltip}];
        const newGeneratedTabs = [{label: 'Outline', key: 'outline', icon: WrittenFileIcon, score: outlineScore, tooltip: outlineTooltip}, {label: 'AI Version', key: 'generated', tooltip: generatedTooltip, icon: WriterMagicPen, score: generatedScore}, {label: 'Working Draft', key:'new', icon: DraftIcon, score: articleScore, tooltip: draftTooltip}];

        if(articleCompleted === null) {
            // setTypeOptions(defaultTabs);
            setNewTypeOptions(newDefaultTabs);
        } else {
            // setTypeOptions(generatedTabs);
            setNewTypeOptions(newGeneratedTabs);
        }
    }, [lastGeneratedArticle, articleScore, outlineScore, generatedScore, articleCompleted]);

    const showWebsiteCurrentArticleRef = useRef(false);
    const editorArticleModeRef = useRef('new');

    useEffect(() => {
        showWebsiteCurrentArticleRef.current = selectedTypeOption === 'current';
        editorArticleModeRef.current = selectedTypeOption;
    }, [selectedTypeOption]);


    // Use useRef to create a mutable object that holds the latest value of writerMode
    const writerModeRef = useRef(writerMode);

    // Update writerModeRef when writerMode changes
    useEffect(() => {
        setHeadingIsEmpty(false);

        writerModeRef.current = writerMode;
    }, [writerMode]);

    const editorLoading = () => {
        return writerMode === 'article' ? articleLoading : outlineLoading;
    };

    useEffect(() => {
        checkAndSetHeadingIsEmpty();
    }, [parsedData]);

    const checkAndSetHeadingIsEmpty = () => {
        setHeadingIsEmpty(!parsedData.headings1.trim());
    };

    const addContentToEditor = (editor) => {
        //here add uncompiled article
        let content;
        if(writerMode === 'article') {
            if(selectedTypeOption === 'current' && currentWebsiteArticle !== null) {
                content = currentWebsiteArticle;
            } else if(selectedTypeOption === 'generated') {
                content = lastGeneratedArticle;
            } else {
                content = articleData
            }
        } else {
            content = outline || ''
        }

        editor.commands.setContent(content);
        trackDataDetails(editor);
    };

    const isOutlineEditorDisabled = () => {
        if (isOutline && articleCompleted === false && parseInt(completedHeadings) != totalHeadings) {
            return false;
        }
        return true;
    };

    const updateEditor = (addContent = false) => {
        if ((articleData || outline) && editor) {
            if (addContent) addContentToEditor(editor);
            trackDataDetails(editor);

            if (writerModeRef.current === 'article') {
                if (sendOutlineDataToServer) sendOutlineDataToServer.cancel();
                if (isEditorEditable()) {
                    if (!sendDataToServer) {
                        sendDataToServer(dispatch, {type: applicationType, viewOnly: selectedTypeOption !== 'new'});
                    }
                } else {
                    if (sendDataToServer) sendDataToServer.cancel();
                    if (sendOutlineDataToServer) sendOutlineDataToServer.cancel();
                }
            } else {
                if (sendDataToServer) sendDataToServer.cancel();
                if (!sendOutlineDataToServer) sendOutlineDataToServer(dispatch, editor.getHTML());
            }
        }
    };

    const showLoadingArticle = () => {
        return  loadingCurrentWebsiteArticle &&
            selectedTypeOption === 'current' &&
            writerMode === 'article' &&
            currentWebsiteArticle === null
    }

    useEffect(() => {
        console.log('intra aici la final? writerMode => ', writerMode)
        if (writerMode === 'article') updateEditor(!articleEditorIsEditable || articleJustFinishedGenerating);
    }, [articleData]);

    useEffect(() => {
        updateEditor(true);
    }, [writerMode, articleLoading, outlineLoading, generating]);

    useEffect(() => {
        if (writerMode === 'outline') updateEditor();
    }, [outline]);

    useEffect(() => {
        if (writerMode === 'article') updateEditor(true);
    }, [currentWebsiteArticle, selectedTypeOption]);

    useUnmount(() => {
        dispatch(setArticle(null));
        dispatch(setArticleId(null));
        dispatch(setArticlesCollection([]));
        dispatch(setEditorInstance(null));
        dispatch(setTopicCoverageData([]));
        dispatch(setHighTopicCoverage([]));
        dispatch(setLowTopicCoverage([]));
        if (editor) {
            editor.destroy();
        }
    });

    const trackDataDetails = (editor) => {
        const data = editor.getHTML();
        const wordCount = editor.storage.characterCount.words();
        dispatch(setWordCount(wordCount));
        // parsed all data - debounced
        parseEditorData(dispatch, data);
        // track words from the content editor - debounced
        trackTopicCoverage(dispatch);
        // track title from content editor - debounced
        trackTitle(dispatch);
        // track headings from content editor - debounced
        trackHeadings(dispatch);
        // track topic density from content editor
        // we use raw data for this one - debounced
        trackTopicDensity(dispatch);
        // track images from content editor - debounce
        trackImages(dispatch);
    };
    const isEditorEditable = () => {
        const isOutline = writerMode === 'outline';
        const isArticle = writerMode === 'article';

        if (isOutline) {
            return isOutlineEditorDisabled() && articleEditorIsEditable;
        } else {
            return isArticle && articleEditorIsEditable && selectedTypeOption === 'new';
        }
    };


    const editor = useEditor({
        editorProps: {
            transformPastedHTML: (html) => dispatch(transformContentOnPaste(html)),
        },
        editable: isEditorEditable(),
        extensions: [
            CustomDocument,
            StarterKit.configure({
                document: false,
            }),
            CharacterCount,
            CustomImage,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                openOnClick: true,
                linkOnPaste: true,
                HTMLAttributes: {
                    target: '_blank',
                    rel: 'canonical',
                    class: null,
                },
            }),
            Placeholder.configure({
                placeholder: ({ node }) => {
                    if (node.type.name === 'heading') {
                        return 'Insert title here...';
                    }

                    return '';
                },
            }),
        ],
        // content: currentArticle.content,
        onCreate: ({ editor }) => {
            // setting editor content with current article content
            addContentToEditor(editor);
            // set editor instance globally so it can be accessed throught the app
            dispatch(setEditorInstance(editor)); // REVIEW: is this needed?
        },
        onUpdate: ({ editor }) => {
            trackDataDetails(editor);
            // send data to API, autosave debounced
            if (!isDemoApp && (currentArticleId !== DEFAULT_ARTICLE_ID || !!hasSharedToken)) {
                if (writerModeRef.current === 'article') {
                    dispatch(setArticleAltered(true));
                    sendDataToServer(dispatch, {type: applicationType, viewOnly: selectedTypeOption !== 'new'});
                } else {
                    sendOutlineDataToServer(dispatch, editor.getHTML());
                }
            }
        },
    });

    const wrapPlainTextWithParagraphs = (htmlContent) => {
        if(!htmlContent) return '';

        // First clean up any excessive whitespace and newlines
        let cleanContent = htmlContent.replace(/>\s+</g, '><').replace(/\n+/g, ' ').trim();

        // Convert <b> to <strong> and <i> to <em>
        cleanContent = cleanContent
            .replace(/<b\b[^>]*>/g, '<strong>')
            .replace(/<\/b>/g, '</strong>')
            .replace(/<i\b[^>]*>/g, '<em>')
            .replace(/<\/i>/g, '</em>');

        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanContent, 'text/html');

        // Get all top-level text and inline elements
        const topLevelNodes = [...doc.body.childNodes];
        const newBody = doc.createElement('body');
        let currentP = null;

        topLevelNodes.forEach(node => {
            const isInlineElement = node.nodeType === Node.ELEMENT_NODE &&
                ['strong', 'em', 'span', 'a', 'code', 'mark'].includes(node.tagName.toLowerCase());
            const isTextNode = node.nodeType === Node.TEXT_NODE;

            // Skip empty text nodes
            if (isTextNode && !node.textContent.trim()) return;

            // If it's a block element, add it directly
            if (node.nodeType === Node.ELEMENT_NODE && !isInlineElement) {
                if (currentP && currentP.hasChildNodes()) {
                    newBody.appendChild(currentP);
                }
                currentP = null;
                newBody.appendChild(node.cloneNode(true));
                return;
            }

            // Handle inline elements and text nodes
            if (!currentP) {
                currentP = doc.createElement('p');
            }

            if (isTextNode) {
                currentP.appendChild(doc.createTextNode(node.textContent.trim()));
            } else if (isInlineElement) {
                currentP.appendChild(node.cloneNode(true));
            }
        });

        // Append the last paragraph if it exists
        if (currentP && currentP.hasChildNodes()) {
            newBody.appendChild(currentP);
        }

        // Process paragraphs to add spaces around inline elements
        newBody.querySelectorAll('p').forEach(p => {
            const html = p.innerHTML;
            // Add space before inline elements if needed
            let processed = html.replace(/(\S)(<(?:strong|em)\b[^>]*>)/g, '$1 $2');

            // Add space after inline elements only if not followed by punctuation
            processed = processed.replace(/(<\/(?:strong|em)>)([^.,!?:;\s])/g, '$1 $2');

            // Clean up any double spaces
            p.innerHTML = processed.replace(/\s{2,}/g, ' ');
        });

        return newBody.innerHTML;
    };

    const restoreGenerated = () => {
        const newContent = selectedTypeOption === 'generated' ? lastGeneratedArticle : currentWebsiteArticle;

        setSelectedTypeOption('new');

        dispatch(getContentArticleData(newContent));
        editor.commands.setContent(newContent);
        sendDataToServer(dispatch, {type: applicationType, viewOnly: false})
    }
    const onSwitch = (option) => {
        if(option === 'outline') {
            dispatch(applyWriterMode('outline'));
        } else {
            switchToArticle()
            dispatch(applyWriterMode('article'));
        }

        setSelectedTypeOption(option);
    }

    // Update writerModeRef when writerMode changes
    useEffect(() => {
        if (editor) editor.setOptions({ editable: isEditorEditable() });
    }, [writerMode, articleJustFinishedGenerating, isOutlineEditorDisabled]);

    useEffect(() => {
        if(!editorLoading()){
            if (writerModeRef.current === 'article') {
                dispatch(setArticleAltered(true));

                if(selectedTypeOption === 'new') {
                    sendDataToServer(dispatch, {type: applicationType, viewOnly: selectedTypeOption !== 'new'});
                }
            } else {
                sendOutlineDataToServer(dispatch, editor.getHTML());
            }
        }
    }, [articleLoading, outlineLoading])

    if (!editor) {
        return null;
    }

    const CustomFloatingButton = ({ editor }) => {
        const handleClick = () => {
            setGenerating(true);
            // Handle button click logic

            dispatch(getNewTitleForOutline({ id: currentArticleId, testPrompt: selectedTestPrompt.value }))
                .then((response) => {
                    if (!response.error) {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(outline, 'text/html');
                        const h1Element = doc.querySelector('h1');

                        h1Element.innerHTML = response;

                        const updatedString = doc.body.innerHTML;

                        dispatch(getOutlineData(updatedString));
                    }

                    setGenerating(false);
                })
                .catch((err) => {
                    setGenerating(false);
                });
        };

        const buttonText = () => (generating ? 'Regenerating...' : 'Regenerate');


        return (
            <>
                {writerMode === 'outline' && (
                    <div onClick={isOutlineEditorDisabled() ? handleClick : null} className={isOutlineEditorDisabled() ? "refresh-title" : "refresh-title regenerate-disabled"}>
                        <RefreshIcon /> {buttonText()}
                    </div>
                )}
            </>
        );
    };

    const viewOnlyContent = selectedTypeOption === 'generated' ? lastGeneratedArticle : currentWebsiteArticle;
    const processedContent = wrapPlainTextWithParagraphs(viewOnlyContent);

    return (
        <>
            <div className={writerMode === 'outline' ? 'editor-wrapper outline-mode' : 'editor-wrapper'}>
                <EditorHeader {...{ writerMode, setShowPublishModal, selectedTypeOption }} />
                <EditorMenuTop writerMode={writerMode} editor={editor} />
                <Scrollbars>
                <div className="editor-content-container">
                    {/*{!editorLoading() && (*/}
                        <EditorSwitcher
                            currentWebsiteArticle={currentWebsiteArticle}
                            loadingCurrentWebsiteArticle={loadingCurrentWebsiteArticle}
                            selectedTypeOption={selectedTypeOption}
                            setSelectedTypeOption={setSelectedTypeOption}
                            typeOptions={newTypeOptions}
                            setTypeOptions={setNewTypeOptions}
                            restoreGenerated={restoreGenerated}
                            onSwitch={onSwitch}
                        />
                    {/*)}*/}
                    <ArticleProgressBox
                        isOutline={writerMode === 'outline'}
                        outlineProgressStatus={outlineProgressStatus}
                        articleProgressStatus={articleCompleted}
                        articleCompilationStatus={articleCompilationStatus}
                        totalHeadings={totalHeadings}
                        completedHeadings={completedHeadings}
                        selectedTypeOption={selectedTypeOption}
                    />
                    {!editorLoading() && (writerMode === 'outline' || articleCompleted !== false || selectedTypeOption !== 'generated') &&
                        (
                        <>
                            <div className="editor-content__inner-wrapper">
                                {styleAccess && (
                                    <EditorContentInfo
                                        currentWebsiteArticle={currentWebsiteArticle}
                                        loadingCurrentWebsiteArticle={loadingCurrentWebsiteArticle}
                                        selectedTypeOption={selectedTypeOption}
                                        setSelectedTypeOption={setSelectedTypeOption}
                                        typeOptions={typeOptions}
                                        setTypeOptions={setNewTypeOptions}
                                        restoreGenerated={restoreGenerated}
                                        activeConfigurationStep={activeConfigurationStep}
                                        setActiveConfigurationStep={setActiveConfigurationStep}
                                        setPromptType={setPromptType}
                                        regenerateCount={regenerateCount}
                                        setShowPublishModal={setShowPublishModal}
                                    />
                                )}
                                <AiCard
                                    isOutline={writerMode === 'outline'}
                                    switchToArticle={onSwitch}
                                    generating={editorLoading()}
                                    firstLoad={firstLoad}
                                    outlineProgressStatus={outlineProgressStatus}
                                    aiMode={aiMode}
                                    setAiMode={setAiMode}
                                    selectedTestPrompt={selectedTestPrompt}
                                    setSelectedTestPrompt={setSelectedTestPrompt}
                                    activeConfigurationStep={activeConfigurationStep}
                                    setActiveConfigurationStep={setActiveConfigurationStep}
                                    promptType={promptType}
                                    setPromptType={setPromptType}
                                    regenerateCount={regenerateCount}
                                    selectedTypeOption={selectedTypeOption}
                                />
                                <div className="title-label__wrapper">
                                    <div className="title-label">Title</div> {!hasSharedToken && <CustomFloatingButton editor={editor}/>}
                                </div>
                                {(showLoadingArticle() && (
                                    <ArticleLoadingBlocks />
                                )) || (selectedTypeOption !== 'new' && writerModeRef.current === 'article' && (
                                    <div className={`ProseMirror view-only ${loadingCurrentWebsiteArticle && selectedTypeOption === 'current' ? 'loading' : ''}`} dangerouslySetInnerHTML={{ __html: processedContent }}/>
                                )) || (
                                    <EditorContent editor={editor} className={`editor-content ${headingIsEmpty ? 'heading-is-empty' : ''}`} />
                                )}
                            </div>
                            {!hasSharedToken && selectedTypeOption === 'generated' && !!lastGeneratedArticle && (
                                <FeedbackBox writerMode={writerMode} />
                            )}
                        </>
                    )}
                </div>
                </Scrollbars>
            </div>
        </>
    );
};

export default TipTapEditor;
